import type { ServiceRequestDetail } from '@/entities/service-request';

export type SrfUserEntrySection = 'startingPoint' | 'serviceIdentification' | 'sender' | 'receiver' | 'clearingAgent';

export interface SrfUserEntryRow {
  labelKey: string;
  value?: string;
}

export type SrfUserEntry = Partial<Record<SrfUserEntrySection, SrfUserEntryRow[]>>;

type Text = string | null;

interface Address {
  town: Text;
  office: Text;
  street: Text;
  latLon: Text;
}

interface Party {
  org: Text;
  contact: Text;
  phone: Text;
  email: Text;
}

const rows = (valuesByLabel: Record<string, Text>): SrfUserEntryRow[] =>
  Object.entries(valuesByLabel).flatMap(([labelKey, value]) =>
    value?.trim() ? [{ labelKey, value: value.trim() }] : []
  );

const addressRows = (direction: 'from' | 'to', { town, office, street, latLon }: Address) =>
  rows({
    [`tracking.servicereq.${direction}.label`]: town,
    [`tracking.servicereq.${direction}.office.label`]: office,
    [`tracking.servicereq.${direction}.street.label`]: street,
    [`tracking.servicereq.${direction}.latlon.label`]: latLon
  });

const partyRows = (role: 'sending' | 'receiving' | 'clearing', { org, contact, phone, email }: Party) =>
  rows({
    [`tracking.servicereq.${role}.org.label`]: org,
    [`tracking.servicereq.${role}.contact.label`]: contact,
    [`tracking.servicereq.${role}.phone.label`]: phone,
    [`tracking.servicereq.${role}.email.label`]: email
  });

const startingPointRows = (srf: ServiceRequestDetail) => {
  switch (srf.serviceTypeCode) {
    case 'S':
      return rows({ 'newcmr.arrivingAt.label': srf.storageLocationName });
    case 'SP':
      return addressRows('from', {
        town: srf.storagePickupTown,
        office: srf.storagePickupOffice,
        street: srf.storagePickupAddress,
        latLon: srf.storagePickupLatLon
      });
    default:
      return addressRows('from', {
        town: srf.fromTown,
        office: srf.fromOffice,
        street: srf.fromAddress,
        latLon: srf.fromLatLon
      });
  }
};

const serviceIdentificationRows = (srf: ServiceRequestDetail) => {
  if (srf.serviceTypeCode !== 'T') {
    return rows({ 'request.destinationLocation.label': srf.storageLocationName });
  }
  return addressRows('to', { town: srf.toTown, office: srf.toOffice, street: srf.toAddress, latLon: srf.toLatLon });
};

const receiverRows = (srf: ServiceRequestDetail) => {
  if (srf.receiverSameAsSender) {
    return [{ labelKey: 'tracking.servicereq.receiving.contact.same.label' }];
  }
  return partyRows('receiving', {
    org: srf.receiverOrgName,
    contact: srf.receiverContactName,
    phone: srf.receiverContactPhone,
    email: srf.receiverContactEmail
  });
};

/**
 * What the requester typed into the online SRF, laid out per form card the way legacy RITA's
 * "Online SRF user entry" callouts do on accept. Legacy shows them whether or not the value made it
 * into the field, so nothing here depends on what the form resolved. Empty values are dropped.
 */
export const toSrfUserEntry = (srf: ServiceRequestDetail): SrfUserEntry => ({
  startingPoint: startingPointRows(srf),
  serviceIdentification: serviceIdentificationRows(srf),
  sender: partyRows('sending', {
    org: srf.senderOrgName,
    contact: srf.senderContactName,
    phone: srf.senderContactPhone,
    email: srf.senderContactEmail
  }),
  receiver: receiverRows(srf),
  clearingAgent: partyRows('clearing', {
    org: srf.clearingOrgName,
    contact: srf.clearingContactName,
    phone: srf.clearingContactPhone,
    email: srf.clearingContactEmail
  })
});
